import * as React from 'react';

import { Link, useLocation } from 'react-router-dom';
import { goBack, CallHistoryMethodAction } from 'connected-react-router';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import AppState from '@webapp/store/state';
import { UIMenus } from '@webapp/store/ui/types';
import { hasFlexEnd } from '@webapp/store/ui/selectors';
import { OpenMenu, CloseMenu } from '@webapp/store/ui/action-creators';

import styled, { css } from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import Flex from '../flex';
import Container from '../container';
import Logo from '@components/basics/logo';
import Icon from '@components/basics/icons';
import Backdrop from '@components/basics/backdrop';
import LogoIcon from '@components/basics/logoIcon';
import Typography from '@components/basics/typography';

export interface HeaderProps {
  children?: any;
  className?: string;
  title?: string;
  subtitle?: string;
  distributeContent?: boolean;
  goBack: () => CallHistoryMethodAction;
  actions?: React.ReactElement;
  mobile?: boolean;
  mobileMenu?: any;
  loggedIn: boolean;
  showBackdrop: boolean;
  isOnboarding: boolean;
  noLinkLogo: boolean;
  isMobile: boolean;
  isRelease?: boolean;
  isUpgradeRelease: boolean;
  openedMenu: UIMenus;
  openMainMenu: () => OpenMenu;
  closeAllMenus: () => CloseMenu;
}

const mapStateToProps = (state: AppState) => ({
  loggedIn: !!state.auth.user,
  showBackdrop: state.ui.showBackdrop,
  isMobile: state.ui.mobile,
  openedMenu: state.ui.menuOpened,
  isOnboarding:
    state.router.location.pathname.startsWith('/onboarding') ||
    state.router.location.pathname.endsWith('/unsubscribe/merch'),
  isUpgradeRelease:
    state.router.location.pathname.endsWith('/upgrade-release') ||
    state.router.location.pathname.endsWith('/release-subscription-plan'),
  noLinkLogo:
    state.router.location.pathname.startsWith('/plans') ||
    state.router.location.pathname.startsWith('/payment') ||
    state.router.location.pathname.startsWith('/onboarding'),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goBack: () => dispatch(goBack()),
  openMainMenu: () => dispatch(new OpenMenu('MAIN_MENU')),
  closeAllMenus: () => dispatch(new CloseMenu()),
});

export const Header: React.FC<HeaderProps> = (props) => {
  const location = useLocation();
  const mainMenuOpened = props.openedMenu === 'MAIN_MENU';
  const menuOutside =
    props.openedMenu === 'ADDNEW_MENU' ||
    props.openedMenu?.startsWith('CATALOG_TRACK_MENU');

  const isOnboarding = props.isOnboarding;
  const isUpgradeRelease = props.isUpgradeRelease;

  const toggleMainMenu = (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    ev.stopPropagation();

    if (!mainMenuOpened) {
      return props.openMainMenu();
    }

    return props.closeAllMenus();
  };

  const HeaderClasses = () => {
    if (hasFlexEnd(location)) {
      return props.className + ' flex-end';
    }
    return props.className;
  };

  const MenusWithBackdrop = () => {
    if (
      props.openedMenu === 'MAIN_MENU' ||
      props.openedMenu === 'ARTIST_MENU' ||
      props.openedMenu?.startsWith('ARTIST_MENU') ||
      props.openedMenu === 'EDIT_ARTIST_PROFILE' ||
      props.openedMenu?.startsWith('EDIT_ARTIST_PROFILE') ||
      props.openedMenu === 'EDIT_USER_PROFILE' ||
      props.openedMenu?.startsWith('EDIT_USER_PROFILE') ||
      props.openedMenu === 'MERCH_TRIAL' ||
      props.openedMenu?.startsWith('MERCH_TRIAL') ||
      props.openedMenu === 'DISTRIBUTION_TRIAL' ||
      props.openedMenu?.startsWith('DISTRIBUTION_TRIAL') ||
      props.openedMenu === 'NOTIFICATIONS_MENU' ||
      props.openedMenu?.startsWith('NOTIFICATIONS_MENU') ||
      props.openedMenu === 'VERIFICATION_MENU' ||
      props.openedMenu?.startsWith('VERIFICATION_MENU') ||
      props.openedMenu === 'SYNC_NOTIFICATION_MENU' ||
      props.openedMenu?.startsWith('SYNC_NOTIFICATION_MENU') ||
      props.openedMenu === 'USER_WITHDRAW' ||
      props.openedMenu?.startsWith('USER_WITHDRAW') ||
      props.openedMenu === 'NEW_ROSTER' ||
      props.openedMenu?.startsWith('NEW_ROSTER') ||
      props.openedMenu === 'ROSTER' ||
      props.openedMenu?.startsWith('ROSTER') ||
      props.openedMenu === 'ANALYTICS_MENU' ||
      props.openedMenu?.startsWith('ANALYTICS_MENU') ||
      props.openedMenu === 'CATALOG_MENU' ||
      props.openedMenu?.startsWith('CATALOG_MENU') ||
      props.openedMenu === 'CATALOG_TRANSFER_MENU' ||
      props.openedMenu?.startsWith('CATALOG_TRANSFER_MENU') ||
      props.openedMenu === 'DRAFTS_MENU' ||
      props.openedMenu?.startsWith('DRAFTS_MENU') ||
      props.openedMenu === 'MERCH_SUCCESS_MENU' ||
      props.openedMenu?.startsWith('MERCH_SUCCESS_MENU') ||
      props.openedMenu === 'SUCCESS_SUBSCRIPTION_MERCH_PRO' ||
      props.openedMenu?.startsWith('SUCCESS_SUBSCRIPTION_MERCH_PRO') ||
      props.openedMenu === 'SUCCESS_SUBSCRIPTION_DIST_PRO' ||
      props.openedMenu?.startsWith('SUCCESS_SUBSCRIPTION_DIST_PRO') ||
      props.openedMenu?.startsWith('DEAL_DETAILS')
    ) {
      return true;
    }
    return false;
  };

  return (
    <header className={HeaderClasses()}>
      <Container className="container">
        <Flex className="header-container">
          {props.title && !mainMenuOpened ? (
            <>
              <Icon iconType="arrow-left" onClick={() => props.goBack()} />
              <Flex flexDirection="column" alignItems="center">
                <Typography weight="700">{props.title}</Typography>
                <Typography weight="700" type="small">
                  {props.title}
                </Typography>
              </Flex>
            </>
          ) : props.noLinkLogo ? (
            <Logo className="logo" color="red" />
          ) : (
            <Link
              className="logo-wrapper"
              to={props.loggedIn ? '/dashboard' : '/login'}
            >
              {props.loggedIn ? (
                <LogoIcon className="logo" color="red" />
              ) : (
                <Logo className="logo" color="red" />
              )}
            </Link>
          )}
          {props.isMobile && !props.noLinkLogo && props.loggedIn && (
            <Flex
              className="mobile-wrapper"
              alignItems="center"
              // onClick={(ev) => ev.stopPropagation()}
            >
              {!props.title ? (
                <div className="actions-wrapper">{props.actions}</div>
              ) : null}
              {!isUpgradeRelease && (
                <Icon
                  className="menu-icn"
                  iconType={mainMenuOpened ? 'close' : 'handle'}
                  onClick={(ev) => toggleMainMenu(ev)}
                />
              )}
            </Flex>
          )}
        </Flex>

        <Flex
          className={`navbar-container ${mainMenuOpened ? 'open' : ''}`}
          alignItems="center"
        >
          {props.mobileMenu && props.mobile ? (
            <div className="navbar-container-mobile-menu">
              {props.mobileMenu}
            </div>
          ) : (
            props.children
          )}
          {props.mobile ? null : (
            <div className="actions-wrapper">{props.actions}</div>
          )}
          {props.mobile && isOnboarding && (
            <div className="actions-wrapper">{props.actions}</div>
          )}
        </Flex>
      </Container>
      <AnimatePresence initial={false} exitBeforeEnter>
        {props.openedMenu !== 'ADD_LINK' &&
        !props.openedMenu?.startsWith('EDIT_LINK_PROPERTIES') &&
        MenusWithBackdrop() ? (
          <Backdrop
            isMenuOutside={menuOutside}
            onClick={() => props.closeAllMenus()}
          />
        ) : null}
      </AnimatePresence>
    </header>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(Header)`
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndices.dialogs};
  background-color: ${({ theme }) => theme.colors.differentBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.headerBorder};

  @media ${({ theme }) => theme.device.tablet} {
    height: ${({ theme }) => theme.sizes.navbar};
    max-height: ${({ theme }) => theme.sizes.navbar};
    padding: 0 32px;
  }

  &.flex-end {
    .container {
      .navbar-container {
        @media ${({ theme }) => theme.device.tablet} {
          justify-content: flex-end;
        }
      }
    }
    .navbar-container {
      &.open {
        height: calc(100vh - 88px);
      }
    }
  }

  .container {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
    position: relative;
    z-index: ${({ theme }) => theme.zIndices.header};
    height: auto;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.differentBackground};
    transition: height
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    @media ${({ theme }) => theme.device.tablet} {
      height: 100%;
      z-index: unset;
      justify-content: space-between;
      align-items: center;
      flex-direction: row;
    }

    ${(props) =>
      props.isOnboarding
        ? css`
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          `
        : null}
  }

  .header-container {
    padding: 24px 18px;
    width: 100%;
    box-sizing: border-box;
    margin-right: auto;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1;

    .menu-icn {
      width: 40px;
      height: 40px;
      cursor: pointer;
    }

    @media ${({ theme }) => theme.device.tablet} {
      padding: 0;
      width: auto;
    }

    @media ${({ theme }) => theme.device.desktopL} {
      padding-left: 140px;
    }

    .logo-wrapper {
      height: 25px;
    }

    .logo {
      flex-shrink: 0;
      flex-grow: 0;
      height: 25px;
    }
  }

  .navbar-container {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
    pointer-events: none;
    height: 0;
    opacity: 0;
    background: transparent;
    position: absolute;
    top: 88px;
    right: 0;
    left: 0;
    transition: height
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`},
      opacity
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`},
      background
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};

    &.open {
      background: ${({ theme }) => theme.colors.header};
      opacity: 1;
      pointer-events: all;
      overflow-y: scroll;
      height: auto;
    }

    @media ${({ theme }) => theme.device.tablet} {
      height: 100%;
      opacity: 1;
      padding: 0;
      margin-left: 80px;
      justify-content: ${(props) =>
        props.distributeContent ? 'space-between' : 'flex-end'};
      align-items: center;
      flex-direction: row;
      pointer-events: all;
      position: relative;
      top: auto;
      right: auto;
      left: auto;
      background: transparent;
    }

    ${(props) =>
      props.isOnboarding
        ? css`
            position: relative;
            top: auto;
            right: auto;
            left: auto;
            padding: 24px 18px 24px 0;
            height: auto;
            opacity: 1;
            align-items: flex-end;

            @media ${({ theme }) => theme.device.tablet} {
              justify-content: flex-end;
            }
          `
        : null}
  }

  .navbar-container-mobile-menu {
    padding: 18px;
    width: 100%;
  }
`);
